import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Copy, Play, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface VideoItem {
  title: string;
  description: string;
  url: string;
  timestamp?: string;
  thumbnail?: string;
}

const VideosEngracadosPage = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  const handlePlayVideo = (video: VideoItem) => {
    setSelectedVideo(video);
    toast.success(`Reproduzindo: ${video.title}`);
  };

  const shortFunnyVideos: VideoItem[] = [
    {
      title: "Short Cute Videos Engraçados",
      description: "Compilação de vídeos curtos e engraçados",
      url: "https://www.youtube.com/watch?v=-wMImqllxDA",
      timestamp: "6:35",
      thumbnail: "https://i.ytimg.com/vi/-wMImqllxDA/hqdefault.jpg"
    },
    {
      title: "Super Mario Bros 3",
      description: "Gameplay clássica do Super Mario Bros 3",
      url: "https://www.youtube.com/watch?v=bDctcCM3zMY",
      timestamp: "10:00",
      thumbnail: "https://i.ytimg.com/vi/bDctcCM3zMY/hqdefault.jpg"
    },
    {
      title: "Super Mario Bros Gameplay",
      description: "Jogando Super Mario Bros clássico",
      url: "https://www.youtube.com/watch?v=X62A24QY0Lo",
      thumbnail: "https://i.ytimg.com/vi/X62A24QY0Lo/hqdefault.jpg"
    },
    {
      title: "Vídeo Engraçado 1",
      description: "Momentos hilários compilados",
      url: "https://youtu.be/lCUizaxs-20",
      thumbnail: "https://i.ytimg.com/vi/lCUizaxs-20/hqdefault.jpg"
    },
    {
      title: "Vídeo Engraçado 2",
      description: "Mais momentos divertidos e engraçados",
      url: "https://youtu.be/n7Vjj_Kpj8Y",
      thumbnail: "https://i.ytimg.com/vi/n7Vjj_Kpj8Y/hqdefault.jpg"
    },
    {
      title: "Vídeo Engraçado 3",
      description: "Compilação de momentos hilários",
      url: "https://youtu.be/wavrtdB-gJ4",
      timestamp: "19:46",
      thumbnail: "https://i.ytimg.com/vi/wavrtdB-gJ4/hqdefault.jpg"
    }
  ];

  const liveStreams: VideoItem[] = [
    {
      title: "Transmissão ao vivo",
      description: "Live streaming de conteúdo variado",
      url: "https://www.youtube.com/watch?v=k7uptCGZEFc",
      timestamp: "56:28",
      thumbnail: "https://i.ytimg.com/vi/k7uptCGZEFc/hqdefault.jpg"
    },
    {
      title: "Live JplmlgTLctA",
      description: "Transmissão ao vivo com conteúdo interativo",
      url: "https://www.youtube.com/live/JplmlgTLctA",
      timestamp: "2:46:04",
      thumbnail: "https://i.ytimg.com/vi/JplmlgTLctA/hqdefault.jpg"
    },
    {
      title: "Raspberry Pi Live",
      description: "Transmissão sobre projetos com Raspberry Pi",
      url: "https://www.youtube.com/live/FbiPKTXpBhM",
      timestamp: "1:30:52",
      thumbnail: "https://i.ytimg.com/vi/FbiPKTXpBhM/hqdefault.jpg"
    },
    {
      title: "Live ESufCPlRaFw",
      description: "Transmissão ao vivo de conteúdo variado",
      url: "https://www.youtube.com/live/ESufCPlRaFw",
      thumbnail: "https://i.ytimg.com/vi/ESufCPlRaFw/hqdefault.jpg"
    }
  ];

  const tutorialLinks: VideoItem[] = [
    {
      title: "Criando Vídeos com Fotos no Leonardo AI",
      description: "Tutorial completo para criar vídeos a partir de fotos usando Leonardo AI",
      url: "https://livrariagokage.blogspot.com/2025/06/criando-videos-com-fotos-no-leonardo-ai.html",
      thumbnail: "https://placehold.co/600x400/e9d8fd/6b46c1?text=Tutorial+Leonardo+AI"
    }
  ];

  const renderVideoModal = () => {
    if (!selectedVideo) return null;
    
    const videoId = selectedVideo.url.includes("youtu.be/") 
      ? selectedVideo.url.split("youtu.be/")[1]
      : selectedVideo.url.includes("youtube.com/watch?v=")
        ? selectedVideo.url.split("v=")[1].split("&")[0]
        : selectedVideo.url.includes("youtube.com/live/")
          ? selectedVideo.url.split("live/")[1].split("?")[0]
          : "";

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg overflow-hidden w-full max-w-4xl">
          <div className="p-4 flex justify-between items-center border-b">
            <h3 className="text-lg font-medium">{selectedVideo.title}</h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedVideo(null)}>
              ✕
            </Button>
          </div>
          <div className="aspect-video w-full">
            {videoId ? (
              <iframe 
                width="100%" 
                height="100%" 
                src={`https://www.youtube.com/embed/${videoId}`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <p>Este vídeo não pode ser incorporado. <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer" className="text-primary">Abrir link externo</a></p>
              </div>
            )}
          </div>
          <div className="p-4">
            <p className="text-muted-foreground">{selectedVideo.description}</p>
            {selectedVideo.timestamp && (
              <p className="text-sm mt-2">Duração destacada: {selectedVideo.timestamp}</p>
            )}
          </div>
          <div className="p-4 border-t flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => handleCopy(selectedVideo.url)}
            >
              <Copy size={16} className="mr-2" /> Copiar Link
            </Button>
            <Button 
              asChild 
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <a href={selectedVideo.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={16} className="mr-2" /> Abrir no YouTube
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Vídeos Engraçados e Transmissões
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Coleção de vídeos curtos engraçados, transmissões ao vivo e tutoriais para seu entretenimento.
          </p>
        </div>

        <Tabs defaultValue="shorts" className="w-full mb-12">
          <div className="flex justify-center mb-8">
            <TabsList>
              <TabsTrigger value="shorts">Vídeos Curtos</TabsTrigger>
              <TabsTrigger value="lives">Transmissões</TabsTrigger>
              <TabsTrigger value="tutorials">Tutoriais</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="shorts" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {shortFunnyVideos.map((video, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative cursor-pointer" onClick={() => handlePlayVideo(video)}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x400/e9d8fd/6b46c1?text=${encodeURIComponent(video.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button className="bg-primary/90 hover:bg-primary text-primary-foreground">
                        <Play className="mr-2 h-4 w-4" /> Reproduzir
                      </Button>
                    </div>
                    {video.timestamp && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 text-xs rounded">
                        {video.timestamp}
                      </div>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{video.title}</CardTitle>
                    <CardDescription>{video.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(video.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={() => handlePlayVideo(video)}
                      >
                        <Play size={16} className="mr-2" /> Assistir
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="lives" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveStreams.map((video, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative cursor-pointer" onClick={() => handlePlayVideo(video)}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/600x400/e9d8fd/6b46c1?text=${encodeURIComponent(video.title)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <Button className="bg-primary/90 hover:bg-primary text-primary-foreground">
                        <Play className="mr-2 h-4 w-4" /> Reproduzir
                      </Button>
                    </div>
                    <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 text-xs rounded-full animate-pulse">
                      LIVE
                    </div>
                    {video.timestamp && (
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 text-xs rounded">
                        {video.timestamp}
                      </div>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{video.title}</CardTitle>
                    <CardDescription>{video.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{video.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(video.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      <Button 
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                        onClick={() => handlePlayVideo(video)}
                      >
                        <Play size={16} className="mr-2" /> Assistir
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="tutorials" className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tutorialLinks.map((tutorial, index) => (
                <Card key={index} className="overflow-hidden flex flex-col h-full">
                  <div className="aspect-video relative">
                    <img 
                      src={tutorial.thumbnail} 
                      alt={tutorial.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{tutorial.title}</CardTitle>
                    <CardDescription>{tutorial.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2 pt-0 flex-grow">
                    <p className="text-sm text-muted-foreground truncate">{tutorial.url}</p>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <div className="flex space-x-2 w-full">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => handleCopy(tutorial.url)}
                      >
                        <Copy size={16} className="mr-2" /> Copiar Link
                      </Button>
                      <Button 
                        asChild
                        className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                      >
                        <a href={tutorial.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink size={16} className="mr-2" /> Acessar
                        </a>
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
            
            <Card className="mt-8">
              <CardHeader>
                <CardTitle>Criando Vídeos com Fotos no Leonardo AI</CardTitle>
                <CardDescription>
                  Aprenda a criar vídeos incríveis usando apenas fotos e a plataforma Leonardo AI
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium mb-2">O que você vai aprender:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Como criar uma conta no Leonardo AI</li>
                      <li>Como preparar suas imagens para animação</li>
                      <li>Técnicas para criar transições suaves</li>
                      <li>Como adicionar efeitos especiais aos seus vídeos</li>
                      <li>Exportação e compartilhamento do resultado final</li>
                    </ul>
                  </div>
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-medium mb-2">Requisitos:</h3>
                    <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                      <li>Fotos de boa qualidade (mínimo 1024x1024px)</li>
                      <li>Conta no Leonardo AI (gratuita ou premium)</li>
                      <li>Conhecimentos básicos de edição de imagem</li>
                      <li>Navegador atualizado (Chrome ou Firefox recomendados)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button 
                  asChild 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://livrariagokage.blogspot.com/2025/06/criando-videos-com-fotos-no-leonardo-ai.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Acessar Tutorial Completo
                  </a>
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="bg-muted p-6 rounded-lg mt-12">
          <h2 className="text-2xl font-semibold mb-4">Sobre esta coleção</h2>
          <p className="text-muted-foreground mb-4">
            Esta página reúne uma seleção de vídeos curtos engraçados, transmissões ao vivo e tutoriais para seu entretenimento e aprendizado.
            Os vídeos são incorporados diretamente do YouTube e outros sites, oferecendo uma experiência integrada sem sair do nosso portal.
          </p>
          <h3 className="text-lg font-medium mt-6 mb-2">Tem um vídeo para sugerir?</h3>
          <p className="text-muted-foreground mb-4">
            Envie suas sugestões de vídeos engraçados, transmissões ou tutoriais para nossa equipe avaliar e possivelmente adicionar à coleção.
          </p>
          <Button onClick={() => toast.success("Funcionalidade em desenvolvimento. Volte em breve!")}>
            Sugerir vídeo
          </Button>
        </div>
      </main>
      <Footer />
      {renderVideoModal()}
    </div>
  );
};

export default VideosEngracadosPage;